import React, { useEffect, useState } from 'react';
import { CircleCheck, Info, TriangleAlert, X } from 'lucide-react';

export type ToastTone = 'info' | 'success' | 'error';

interface ToastProps {
  id: number;
  tone: ToastTone;
  /** A few words in bold before the message. */
  title?: string;
  message: string;
  /** How long it stays. `null` keeps it until the person closes it. */
  durationMs: number | null;
  onClose: (id: number) => void;
}

const ICONS = { info: Info, success: CircleCheck, error: TriangleAlert };

/**
 * One notice (item 74). Always an icon and text, so the tone never rests on colour alone. It waits
 * while the pointer is over it or something inside has the focus, so it cannot vanish under a person
 * who is reading it or reaching for its button. Errors are announced at once; the rest politely.
 */
const Toast: React.FC<ToastProps> = ({ id, tone, title, message, durationMs, onClose }) => {
  const [held, setHeld] = useState(false);
  const Icon = ICONS[tone];

  useEffect(() => {
    if (held || durationMs == null) return;
    const timer = window.setTimeout(() => onClose(id), durationMs);
    return () => window.clearTimeout(timer);
  }, [held, durationMs, id, onClose]);

  return (
    <div
      className={`ui-toast is-${tone}`}
      role={tone === 'error' ? 'alert' : 'status'}
      onMouseEnter={() => setHeld(true)}
      onMouseLeave={() => setHeld(false)}
      onFocus={() => setHeld(true)}
      onBlur={() => setHeld(false)}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose(id);
      }}
    >
      <Icon className="ui-toast-icon" size={16} strokeWidth={1.5} aria-hidden />
      <p className="ui-toast-text">
        {title && <b>{title} </b>}
        {message}
      </p>
      <button type="button" className="ui-close" onClick={() => onClose(id)} aria-label="Dismiss notice">
        <X size={16} strokeWidth={1.5} aria-hidden />
      </button>
    </div>
  );
};

export default Toast;
